import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { cancelScrapingRun, exportScrapingRunResults } from '../apis';
import { SCRAPING_RUN_STATUS, type ScrapingRunSummary } from '../types';
import { scrapingQueryKeys } from './scraping-query-keys';
import { downloadScrapingRunCsv } from './scraping-result-file';

const CANCELABLE_RUN_STATUSES = new Set<SCRAPING_RUN_STATUS>([
  SCRAPING_RUN_STATUS.PENDING,
  SCRAPING_RUN_STATUS.SUBMITTING,
  SCRAPING_RUN_STATUS.SUBMITTED,
  SCRAPING_RUN_STATUS.LIBRARIAN_PENDING,
  SCRAPING_RUN_STATUS.RUNNING_VALIDATING,
  SCRAPING_RUN_STATUS.RUNNING_PRE_SCRAPING,
  SCRAPING_RUN_STATUS.RUNNING_SUBMITTING,
  SCRAPING_RUN_STATUS.RUNNING_SCRAPING,
  SCRAPING_RUN_STATUS.RUNNING_REGISTERING_RESULT,
  SCRAPING_RUN_STATUS.RUNNING_ANALYZING,
  SCRAPING_RUN_STATUS.RUNNING_INDEXING,
]);

export function canCancelRun(run: ScrapingRunSummary) {
  return CANCELABLE_RUN_STATUSES.has(run.status);
}

export function canExportRun(run: ScrapingRunSummary) {
  return run.status === SCRAPING_RUN_STATUS.COMPLETED;
}

export function useScrapingRunActions(intent_id: string) {
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationFn: (runId: string) => cancelScrapingRun(runId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: scrapingQueryKeys.intentDetail(intent_id) }),
      ]);
      toast.success('Scraping run cancellation requested.');
    },
  });

  const exportMutation = useMutation({
    mutationFn: async (run: ScrapingRunSummary) => {
      const csv = await exportScrapingRunResults(run.run_id);
      downloadScrapingRunCsv(run, csv);
    },
    onSuccess: () => {
      toast.success('Scraping run CSV exported.');
    },
  });

  return {
    cancelMutation,
    exportMutation,
  };
}
